/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {StateService} from '../../../core/state/state.service';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {SelectableOptions} from '../../../models/selectable-options';
import {User} from '../../../models/user.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-propulsion-duplicate-check',
    templateUrl: './propulsion-duplicate-check.component.html',
    styleUrls: ['./propulsion-duplicate-check.component.scss'],
    standalone: false
})
export class PropulsionDuplicateCheckComponent implements OnInit {
  user: User;

  showSpinner: boolean;

  propScenWithDupes: PropulsionScenario[];
  duplicateRecordData: any[];
  selectedRow: any;

  file: File;
  sheets: SelectableOptions = new SelectableOptions();

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };
  propScenColumns: ColDef[] = [
    {field: 'id', headerName: 'PROPULSION SCENARIO ID'},
    {field: 'propulsionScenarioName', headerName: 'NAME'},
    {field: 'forecastOrganization', headerName: 'FCORG'}
  ];

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              private readonly propulsionScenarioService: PropulsionScenarioService,
              private readonly userService: StateService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.selectedRow = null;
    this.getPropScenListForDupeCheck();
  }

  /**
   * Get list of Propulsion scenarios that are indicated to contain duplicate records
   */
  getPropScenListForDupeCheck(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getPropScenListForDupeCheck().subscribe(data => {
      this.propScenWithDupes = data;
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Get duplicate records of the specified Propulsion Scenario
   */
  getPropDupeRecords(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getPropDupeRecords(this.selectedRow.id).subscribe(data => {
      this.duplicateRecordData = data;
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.showSpinner = false;
      if (this.duplicateRecordData.length > 0) {
        this.exportDuplicateRecords();
      } else {
        this.snackBar.displayError('This scenario contains no duplicate records, ' +
          'please re-run the scan to refresh the table.', DISMISS_MSG);
      }
    });
  }

  /**
   * Scan all Propulsion Scenarios for any duplicate records
   */
  scanPropScenDupes(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.scanForPropScenDupes().subscribe(() => {
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.getPropScenListForDupeCheck();
    });
  }

  /**
   * Function used to export duplicateRecordData to Excel
   */
  exportDuplicateRecords(): void {
    this.showSpinner = true;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const exportRequest = new WorksheetExportRequest();
    exportRequest.fileName = 'PropDupeRecords_' + currDate;
    const data = new Map<string, any[]>();
    data['DupRecords'] = this.duplicateRecordData;
    exportRequest.data = data;

    // We'll need to programatically order the year columns towards the end, because for some reason
    // the Calendar Years get put ahead in front of the other columns when getting the data from the service
    const headers: Map<string, string[]> = new Map<string, string[]>();
    // Get list of Calendar years
    const years = [];
    const colNames: string[] = [];
    Object.keys(this.duplicateRecordData[0]).forEach(colName => {
      if (!isNaN(Number(colName))) {
        years.push(colName);
      } else {
        colNames.push(colName);
      }
    });
    // Add the Calendar Years to the end of the column names
    colNames.push(...years);
    headers['DupRecords'] = colNames;
    exportRequest.headers = headers;
    exportRequest.fieldNames = headers;
    this.propulsionScenarioService.transformToExcel(exportRequest).subscribe(
      (file: any) => {
        this.propulsionScenarioService.exportToExcelFromServer(file, exportRequest.fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Event handler for when an upload event is emitted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: false
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        // Clear out the selected options before setting them in case the user already uploaded a file and
        // selected an option previously
        this.sheets.selectedOptions = [];
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0) {
          this.savePropModifications();
        }
      }
    );
  }

  /**
   * Save the propulsion scenario modifications for an Excel sheet during duplicate clean up
   */
  savePropModifications(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.savePropModifications(this.file, this.sheets.selectedOptions, this.user.gmid).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Successfully saved modifications. Performing re-scan of duplicates...', DISMISS_MSG);
        this.scanPropScenDupes()
      }
    });
  }

  /**
   * Event for selecting a scenario
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedRow = (event.data);
    } else if (event.data === this.selectedRow) {
      this.selectedRow = null;
    }
  }
}
